import _ws from '@netuno/ws-client';
import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams } from 'react-router-dom';

import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';

import SendIcon from "../Icons/SendIcon";
import ClipIcon from "../Icons/ClipIcon";
import LoadingAnswer from "../LoadingAnswer";

import config from "../../config.json";
import "./index.css";


const Status = {
  CONNECTING: 'connecting',
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
  RECONNECTING: 'reconnecting'
};

const MessageStatus = {
  IDLE: "idle",
  THINKING: "thinking",
  SEARCHING: "searching",
  RESPONDING: "responding",
  COMPLETED: "completed",
}

const WELCOME_MESSAGE = "Olá! Sou um assistente com tecnologia de IA e posso ajudá-lo com dicas e perguntas.";

const str = (v) => v == null ? "" : typeof v === "string" ? v : JSON.stringify(v);

function Chat() {
  const CHAT_URL_WS = config.api.ws;
  const { CHAT_UID } = useParams();
  const [searchParams] = useSearchParams();
  const sessionParam = searchParams.get("session");
  const [sessionUid, setSessionUid] = useState(null)

  const [chatStatus, setChatStatus] = useState(Status.CONNECTING);
  const [chatNotFound, setChatNotFound] = useState(false);
  const [chatData, setChatData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);

  const [isCompressing, setIsCompressing] = useState(false);
  const [contextLimitReached, setContextLimitReached] = useState(false);
  const [searchLimitReached, setSearchLimitReached] = useState(false);

  const chatWindowRef = useRef(null);
  const [input, setInput] = useState("");

  const updateLastBotMessage = (updates) => {
    setMessages(prev => {
      const index = prev.findLastIndex(
        msg => msg.sender === "bot" && msg.status !== MessageStatus.COMPLETED
      );

      if (index === -1) return prev;

      const updated = [...prev];
      const previous = updated[index];

      // O conteúdo chega em pedaços e por isso acumula-se. A exceção é o resetContent,
      // que o servidor usa quando o texto já mostrado era raciocínio de uma pesquisa e
      // não a resposta: nesse caso o balão volta ao início.
      const { resetContent, ...rest } = updates;

      updated[index] = {
        ...previous,
        ...rest,
        content: resetContent
          ? (rest.content ?? "")
          : rest.content != null
            ? previous.content + rest.content
            : previous.content
      };

      return updated;
    });
  };

  const getLastBotMessageContent = () => {
    const last = messages.findLast(
      msg => msg.sender === "bot" && msg.status !== MessageStatus.COMPLETED
    );
    return last?.content ?? "";
  };

  useEffect(() => {
    let cancelled = false;
    let socketOpened = false;

    // Devolve os dados do chat quando ele existe, ou null quando o serviço
    // responde 404 (chat inexistente). Qualquer outra falha é lançada, para
    // não ser confundida com um chat que não existe.
    async function loadChat() {
      const response = await fetch(
        config.api.services + `/chat/get?uid=${encodeURIComponent(CHAT_UID)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error(`Failed to fetch chat settings (HTTP ${response.status})`);
      }

      const responseJSON = await response.json();

      if (responseJSON.result !== true || !responseJSON.chat) {
        return null;
      }

      return responseJSON.chat;
    }

    function onConnect(data) {
      setMessages([]);
      setChatStatus(Status.CONNECTED);
    }

    function onClose() {
      setChatStatus(Status.DISCONNECTED);
    }

    function onError(err) {
      setChatStatus(Status.DISCONNECTED);
    }

    function onMessage(data) {
      if (!data || typeof data !== 'object') return;

      const type = data.type
      const content = str(data.content)

      if (type === "connected") {
        setSessionUid(str(data.sessionUid))

        if (Array.isArray(data.history) && data.history.length > 0) {
          setMessages(data.history.map((msg, index) => ({
            id: index + 1,
            content: str(msg.content),
            sender: msg.role === "assistant" ? "bot" : "user",
            status: MessageStatus.COMPLETED,
            file: Array.isArray(msg.files) && msg.files.length > 0 ? msg.files : undefined
          })));
        } else {
          // Chat novo, sem histórico: mostra a saudação do assistente.
          // Fica como COMPLETED para não ser apanhada pelo updateLastBotMessage
          // nem renderizar o indicador de "a pensar".
          setMessages([
            {
              id: 1,
              content: WELCOME_MESSAGE,
              sender: "bot",
              status: MessageStatus.COMPLETED
            }
          ]);
        }
      }

      if (type === "user_message_saved") {
        const newMessage = {
          sender: "bot",
          content: "",
          status: MessageStatus.IDLE
        };
        setMessages(prev => [
          ...prev,
          { ...newMessage, id: prev.length + 1 }
        ]);
      }

      if (type === "ai_thinking") {
        updateLastBotMessage({ status: MessageStatus.THINKING });
      }

      // O modelo decompõe a pergunta e dispara uma vaga de pesquisas. Mostrar o que
      // está a ser procurado evita o spinner mudo durante uma vaga inteira.
      if (type === "searching") {
        setSearchLimitReached(false);
        updateLastBotMessage({
          status: MessageStatus.SEARCHING,
          searching: str(data.question)
        });
      }

      // O texto já mostrado era raciocínio de um turno de pesquisa, não a resposta.
      if (type === "text_reset") {
        updateLastBotMessage({ resetContent: true, content: "" });
      }

      if (type === "tool_limit_reached") {
        setSearchLimitReached(true);
      }

      // Só chega com settings.chat.debug_search ligado. Vai para a consola em
      // tabela, que é onde se compara scores e se vê porque falhou a resposta.
      if (type === "search_debug") {
        const search = data.search ?? {};
        const args = search.args ?? {};
        const label = `🔍 #${search.index} "${args.question}"`
          + ` (minscore ${args.minscore}, topk ${args.topk})`;

        if (search.refused) {
          console.warn(`${label} → RECUSADA: ${search.refused}`);
          return;
        }

        console.groupCollapsed(
          `${label} → ${search.returned} doc(s)`
          + ` de ${search.totalFound}${search.duplicates ? `, ${search.duplicates} dup` : ""}`
        );
        console.table(search.docs ?? []);
        if (search.highestDiscarded != null) {
          console.info("melhor score descartado:", search.highestDiscarded);
        }
        if (search.note) console.info("nota:", search.note);
        console.groupEnd();
      }

      if (type === "compress_start") {
          setIsCompressing(true);
      }

      if (type === "compress_end") {
          setIsCompressing(false);
      }

      if (type === "context_limit_reached") {
          setIsCompressing(false);
          setContextLimitReached(true);
          setIsLoading(false);
          // Marca a última mensagem do bot como completa para não ficar em loading
          updateLastBotMessage({ status: MessageStatus.COMPLETED });
      }

      if (type === "text") {
        if (!content) return
        updateLastBotMessage({
          status: MessageStatus.RESPONDING,
          searching: null,
          content
        });
      }
   
      if (type === "files_used") {
        updateLastBotMessage({ status: MessageStatus.RESPONDING, file: data.files });
      }

      if (type === "stream_end") {
        updateLastBotMessage({ status: MessageStatus.COMPLETED });
        setIsLoading(false);
      }

    }

    function openWebSocket() {
      _ws.config({
        url: `${CHAT_URL_WS}/${CHAT_UID}${sessionParam ? `?session=${encodeURIComponent(sessionParam)}` : ""}`,
        autoReconnect: true,
        connect: onConnect,
        close: onClose,
        error: onError,
        message: onMessage,
      });

      _ws.connect();
      socketOpened = true;
    }

    setChatStatus(Status.CONNECTING);
    setChatNotFound(false);

    (async () => {
      let chat = null;

      try {
        chat = await loadChat();
      } catch (error) {
        console.error('Error fetching chat settings:', error.message);
        // Não conseguir ler as definições não prova que o chat não existe
        // (rede, serviço em baixo, ...), por isso a ligação segue na mesma.
        if (!cancelled) {
          openWebSocket();
        }
        return;
      }

      if (cancelled) return;

      if (chat == null) {
        // O chat não existe: não vale a pena abrir o WebSocket, o servidor
        // fecharia a ligação e o autoReconnect ficaria a tentar em ciclo.
        console.warn(`Chat ${CHAT_UID} not found: WebSocket not started.`);
        setChatNotFound(true);
        setChatStatus(Status.DISCONNECTED);
        return;
      }

      setChatData(chat);
      openWebSocket();
    })();

    return () => {
      cancelled = true;
      if (socketOpened) {
        _ws.close();
      }
    };
  }, [CHAT_UID, sessionParam]);


  const handleSendMessage = () => {
    if (input.trim()) {
      const trimmed = input.trim();
      setMessages(prev => [
        ...prev,
        {
          id: prev.length + 1,
          content: trimmed,
          sender: "user",
          status: MessageStatus.COMPLETED
        }
      ]);
      setIsLoading(true);
      setSearchLimitReached(false);
      setInput("");

      const textarea = document.querySelector(".chat-input");
      if (textarea) {
        textarea.style.height = "";
      }

      _ws.sendService({
        method: "GET",
        service: "/services/ws/chat",
        data: {
          message: input.trim()
        }
      })

    }
  };

  const scrollToEnd = () => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  };

  const getContrastYIQ = (hexcolor) => {
    hexcolor = hexcolor.replace("#", "");
    const r = parseInt(hexcolor.substr(0, 2), 16);
    const g = parseInt(hexcolor.substr(2, 2), 16);
    const b = parseInt(hexcolor.substr(4, 2), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? 'black' : 'white';
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !isLoading) {
      if (!e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleTextAreaResize = (e) => {
    e.target.style.height = "auto";
    const maxHeight = parseInt(
      window.getComputedStyle(e.target).getPropertyValue("max-height")
    );
    e.target.style.height = Math.min(e.target.scrollHeight, maxHeight) + "px";

    e.target.scrollTop = e.target.scrollHeight;
  };

  if (chatNotFound) {
    return (
      <div className="chat">
        <div className="chat-notice">
          <p>Este chat não existe ou já não está disponível.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`chat ${chatData?.dark_mode && "chat--dark"}`}>
      <header
        className="chat-header"
        style={{ backgroundColor: chatData?.color }}
      >
        <p
          style={{ color: chatData?.color && getContrastYIQ(chatData?.color) }}
        >
          {chatData["name"]}
        </p>
      </header>
      <div
        className={`chat-content ${chatData?.dark_mode && "chat-content--dark"}`}
        ref={chatWindowRef}
      >

        {
          messages.map((message) => {

            if (message.sender === "bot"
                && (message.status === MessageStatus.IDLE
                  || message.status === MessageStatus.THINKING
                  || message.status === MessageStatus.SEARCHING)) {
              return (
                <div className="chat-message-wrapper" key={message.id}>
                  <img
                    style={{ border: `2px solid ${chatData?.color}` }}
                    src="avatars/bot.jpg"
                    alt="AI Face"
                  />
                  <div
                    className={`chat-message bot-message ${chatData?.dark_mode && "bot-message--dark chat-message--dark"
                      }`}
                  >
                    <div
                      className={`bot-message-before ${chatData?.dark_mode && "bot-message-before--dark"
                        }`}
                    ></div>
                    <LoadingAnswer color={chatData?.color} />
                    {message.searching && (
                      <span className="chat-searching">
                        A pesquisar: {message.searching}
                      </span>
                    )}
                  </div>
                </div>
              )
            }
            return (
              <div className={`chat-message-wrapper ${message.sender !== "bot" && "chat-message-wrapper-user"}`} key={message.id}>
                {message.sender === "bot" && (
                  <img
                    style={{ border: `2px solid ${chatData?.color}` }}
                    src="avatars/bot.jpg"
                    alt="AI Face"
                  />
                )}

                <div
                  style={
                    message.sender === "bot"
                      ? {
                        color: getContrastYIQ(
                          chatData?.dark_mode ? "#222222" : "#ffffff"
                        ),
                      }
                      : {
                        color: chatData?.color && getContrastYIQ(chatData?.color),
                        backgroundColor: chatData?.color,
                      }
                  }
                  className={`chat-message ${message.sender === "bot"
                    ? `bot-message ${chatData?.dark_mode && "bot-message--dark"}`
                    : "user-message"
                    }`}
                >
                  {message.sender === "bot" ? (
                    <div
                      className={`bot-message-before ${chatData?.dark_mode && "bot-message-before--dark"
                        }`}
                    ></div>
                  ) : (
                    <div
                      style={{ backgroundColor: chatData?.color }}
                      className="user-message-before"
                    ></div>
                  )}
                  <div className="markdown-body">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        // O espaçamento dos parágrafos vive no CSS: com respostas em
                        // secções, margem zero colava tudo num bloco só.
                        // O react-markdown já não passa `inline`: um bloco de código
                        // vem sempre dentro de um <pre>, por isso é o `pre` que decide
                        // quando usar o realce de sintaxe.
                        pre: ({ children }) => {
                          const child = Array.isArray(children) ? children[0] : children;
                          const className = child?.props?.className ?? "";
                          const language = /language-(\w+)/.exec(className)?.[1];

                          if (!language) {
                            return <pre>{children}</pre>;
                          }

                          return (
                            <SyntaxHighlighter language={language}>
                              {String(child.props.children).trim()}
                            </SyntaxHighlighter>
                          );
                        },
                        // As tabelas podem ser mais largas que a bolha da mensagem,
                        // por isso ficam com scroll horizontal próprio.
                        table: ({ children }) => (
                          <div className="markdown-table-wrapper">
                            <table>{children}</table>
                          </div>
                        )
                      }}
                    >
                      {message["content"].trim()}
                    </ReactMarkdown>
                  </div>


                  {message.file &&
                    chatData["show_links"] &&
                    message.file.map((file, fileIndex) => (
                      <a
                        href={config.api.services + file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        key={fileIndex}
                        className="file-tag-link"
                      >
                        <ClipIcon size={24} />{" "}
                        <div className="file-tag">{file.name}</div>
                      </a>
                    ))}
                </div>

                {message.sender !== "bot" && (
                  <img src="avatars/user.jpg" alt="User Face" />
                )}
              </div>
            )
          })
        }

        {searchLimitReached && (
          <div className="chat-notice">
            Limite de pesquisas atingido: a resposta foi construída apenas com os
            documentos já consultados.
          </div>
        )}

        {isCompressing && (
          <div className="chat-message-wrapper">
              <img
                  style={{ border: `2px solid ${chatData?.color}` }}
                  src="avatars/bot.jpg"
                  alt="AI Face"
              />
              <div className={`chat-message bot-message ${chatData?.dark_mode && "bot-message--dark chat-message--dark"}`}>
                  <div className={`bot-message-before ${chatData?.dark_mode && "bot-message-before--dark"}`} />
                  A resumir memória da conversa…
              </div>
          </div>
      )}
      
      </div>

      <footer
        className={`chat-footer ${chatData?.dark_mode && "chat-footer--dark"}`}
      >
        <textarea
            value={input}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            onKeyUp={handleTextAreaResize}
            placeholder={
                contextLimitReached
                    ? "Limite de contexto atingido. Inicie uma nova conversa."
                    : isLoading
                        ? "Processando a resposta..."
                        : "Escreva a sua pergunta..."
            }
            style={{
                color: getContrastYIQ(chatData?.dark_mode ? "#222222" : "#ffffff"),
            }}
            className={`chat-input ${chatData?.dark_mode && "chat-input--dark"}`}
            disabled={isLoading || contextLimitReached}
            rows={2}
        />
        <button
            onClick={handleSendMessage}
            className="send-button"
            style={{ backgroundColor: chatData?.color }}
            disabled={isLoading || contextLimitReached}
        >
            <SendIcon />
        </button>
      </footer>
    
    </div>
  );
}

export default Chat;